import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LoadingScreen,
  StatusScreen,
} from "@roberttamas/ui";
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Check, MailCheck, ShieldAlert } from "@/components/icons";
import { authErrorMessage } from "@/lib/errors";
import { auth } from "@/lib/firebase";

type Phase =
  | { kind: "loading" }
  | { kind: "reset"; email: string }
  | { kind: "success"; icon: ReactNode; title: string; description: ReactNode }
  | { kind: "error"; title: string; description: ReactNode };

/**
 * Custom Firebase email action handler. Firebase links password-reset, email
 * verification and email-recovery messages here (see the "action URL" set in
 * the Authentication → Templates console) with `?mode=…&oobCode=…`. Public
 * route: recipients are typically signed out when they click the link.
 */
export function AuthActionPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // The oobCode is single-use. Guard against React StrictMode invoking the
  // effect twice in development, which would consume the code on the first run
  // and then fail the second as "invalid action code".
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!oobCode) {
      setPhase({
        kind: "error",
        title: t("authAction.invalidLinkTitle"),
        description: t("authAction.invalidLinkDesc"),
      });
      return;
    }

    const run = async () => {
      try {
        switch (mode) {
          case "resetPassword": {
            const email = await verifyPasswordResetCode(auth, oobCode);
            setPhase({ kind: "reset", email });
            break;
          }
          case "verifyEmail": {
            await applyActionCode(auth, oobCode);
            setPhase({
              kind: "success",
              icon: <MailCheck />,
              title: t("authAction.emailVerifiedTitle"),
              description: t("authAction.emailVerifiedDesc"),
            });
            break;
          }
          case "recoverEmail": {
            const info = await checkActionCode(auth, oobCode);
            await applyActionCode(auth, oobCode);
            const restored = info.data.email ?? t("authAction.yourAccount");
            setPhase({
              kind: "success",
              icon: <Check />,
              title: t("authAction.emailRevertedTitle"),
              description: (
                <Trans
                  i18nKey="authAction.emailRevertedDesc"
                  values={{ email: restored }}
                  components={{ b: <span className="font-medium text-foreground" /> }}
                />
              ),
            });
            break;
          }
          default:
            setPhase({
              kind: "error",
              title: t("authAction.unsupportedTitle"),
              description: t("authAction.unsupportedDesc"),
            });
        }
      } catch (err) {
        setPhase({
          kind: "error",
          title: t("authAction.expiredTitle"),
          description: authErrorMessage(err),
        });
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, oobCode]);

  const handleResetSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || !oobCode) return;
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPhase({
        kind: "success",
        icon: <Check />,
        title: t("authAction.passwordUpdatedTitle"),
        description: t("authAction.passwordUpdatedDesc"),
      });
    } catch (err) {
      // Keep the form on screen so the user can retry with a stronger password.
      toast.error(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (phase.kind === "loading") {
    return <LoadingScreen label={t("authAction.verifyingLink")} />;
  }

  if (phase.kind === "reset") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-base font-semibold">{t("authAction.resetTitle")}</CardTitle>
            <CardDescription>
              <Trans
                i18nKey="authAction.resetFor"
                values={{ email: phase.email }}
                components={{ b: <span className="font-medium text-foreground" /> }}
              />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => void handleResetSubmit(e)} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">{t("authAction.newPassword")}</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("login.passwordPlaceholderNew")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? t("common.saving") : t("authAction.saveNewPassword")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase.kind === "success") {
    return (
      <StatusScreen icon={phase.icon} title={phase.title} description={phase.description}>
        <Button asChild className="w-full">
          <Link to="/login">{t("authAction.goToSignIn")}</Link>
        </Button>
      </StatusScreen>
    );
  }

  return (
    <StatusScreen
      icon={<ShieldAlert />}
      tone="destructive"
      title={phase.title}
      description={phase.description}
    >
      <Button asChild variant="outline" className="w-full">
        <Link to="/login">{t("authAction.backToSignIn")}</Link>
      </Button>
    </StatusScreen>
  );
}
